import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { makeStyles, Box, Typography, IconButton } from '@material-ui/core';
import { MoreHoriz as MoreIcon } from '@material-ui/icons';
import moment from 'moment';

import { toAbsoluteUrl } from '../../../utils/AssetsHelpers';
import { formatCurrencyValue } from '../../../utils/formatter';
import { getStatePaymentData } from '../../../utils/merchants';

import PaymentsListItemActionsMenu from './ActionsMenu';
import styles from './styles';

const useStyles = makeStyles(styles);

const PaymentsListItem = ({
  paymentId,
  createdAt,
  amount,
  currency,
  state,
  description,
  invoiceUrl,
  actionsMenu,
}) => {
  const { t } = useTranslation('common');

  const classes = useStyles();

  const currentStatuData = useMemo(() => getStatePaymentData(state), [state]);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <Box className={classes.listItem}>
      <Box className={classes.listItemRootIconContainer}>
        <Box
          component="img"
          src={toAbsoluteUrl('/assets/icons/pos-payment.svg')}
          alt="Pos Payment Icon"
          className={classes.listItemRootIconImg}
        />
      </Box>

      <Box className={classes.listItemWrapper}>
        <Box className={classes.listItemContent}>
          <Box className={classes.listItemContentColumn}>
            <Typography className={classes.listItemContentLabel}>
              {description}
            </Typography>

            <Typography className={classes.listItemContentDescription}>
              {moment(createdAt).format('DD/MM/YYYY')}
            </Typography>
          </Box>

          <Box className={classes.listItemContentColumnAlignEnd}>
            <Typography className={classes.listItemContentLabelSmall}>
              {`${formatCurrencyValue(amount, {
                style: 'decimal',
              })} ${currency}`}
            </Typography>

            <Box className={classes.listItemContentStatusContainer}>
              <Box
                component="img"
                src={currentStatuData.iconSrc}
                alt="Status Icon"
                className={classes.listItemContentStatusIcon}
              />

              <Typography
                className={classes.listItemContentStatusText}
                style={{ color: currentStatuData.color }}
              >
                {t(currentStatuData.translationKey)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <IconButton
          size="small"
          className={classes.listItemActionMenuIconButton}
          onClick={handleOpenMenu}
        >
          <MoreIcon />
        </IconButton>

        <PaymentsListItemActionsMenu
          anchorEl={anchorEl}
          onClose={handleCloseMenu}
          paymentId={paymentId}
          state={state}
          invoiceUrl={invoiceUrl}
          {...actionsMenu}
        />
      </Box>
    </Box>
  );
};

PaymentsListItem.propTypes = {
  paymentId: PropTypes.number.isRequired,
  createdAt: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  state: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  invoiceUrl: PropTypes.string,
  actionsMenu: PropTypes.shape({
    onShowDetail: PropTypes.func,
    onGoToInvoice: PropTypes.func,
    onCancel: PropTypes.func,
  }),
};

PaymentsListItem.defaultProps = {
  invoiceUrl: null,
  actionsMenu: null,
};

export default PaymentsListItem;
